import { Modal } from 'bootstrap';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import uuid from 'react-uuid';
import api from '../../services/api';
import { INVENTORY_API_BASE_URL_2 } from '../../services/TapApiUrls';
import TapIcon from '../../services/TapIcon';
import Loader from '../../components/ui/Loader/Loader';
import DateService from '../../services/DateService';

const ItemQrCodePrintModal = forwardRef((props, ref) => {

    const accessToken = useSelector(state => state.auth.access_token);
    const currentData = DateService.dateTimeFormat(new Date(), 'DD-MM-YYYY');

    const [loading, setLoading] = useState(false);
    const [printData, setPrintData] = useState({});

    const [printMode, setPrintMode] = useState(null);     
    const [selectedQty, setSelectedQty] = useState(1);
    const [showQrPreview, setShowQrPreview] = useState(false);

    const pendingRequest = useRef(null);

    const [searchModalId, setSearchModalId] = useState(uuid());
    const searchModalElem = useRef(null);
    const searchModal = useRef(null);

    const searchModalOpen = () => searchModal.current && searchModal.current.show();
    const searchModalClose = () => searchModal.current && searchModal.current.hide();

    useEffect(() => {
        searchModalElem.current = document.getElementById(searchModalId);
        searchModal.current = new Modal(searchModalElem.current, { keyboard: false, backdrop: false });
        return () => {
            searchModal.current && searchModal.current.dispose();
        };
    }, []);

    useImperativeHandle(ref, () => ({
        initModalHandler: (item_id, params) => searchModalOpenHandler(item_id, params),
        initModalClose: () => editModalCloseHandler()
    }));

    const searchModalOpenHandler = (item_id, params) => {
        setPrintMode(null);
        setSelectedQty(1);
        setShowQrPreview(false);
        setPrintData({});
        pendingRequest.current = { item_id, params };   
        searchModalOpen();
    };

    const editModalCloseHandler = () => {
        setPrintMode(null);
        setSelectedQty(1);
        setShowQrPreview(false);
        setPrintData({});
        pendingRequest.current = null;
        searchModalClose();
    };
    const fetchQRCode = async () => {
        const req = pendingRequest.current;
        if (!req) return;

        const maxQty = Number(req.params?.total_qty) || 1;

        let qtyToSend = maxQty;
        if (printMode === "each") {
            qtyToSend = 1;
        } else if (printMode === "selected") {
            qtyToSend = Math.min(Number(selectedQty) || 1, maxQty);
        }

        const finalParams = { ...req.params, total_qty: qtyToSend };

        setLoading(true);
        try {
            const response = await api.server2Api.get(
                `${INVENTORY_API_BASE_URL_2}/item_qr_code/${req.item_id}`,
                {
                    headers: { 'Authorization': 'Bearer ' + accessToken },
                    params: finalParams
                }
            );
            setPrintData(response.data);
            setShowQrPreview(true);
        } catch (error) {
            toast.error('Failed to fetch QR code', { position: toast.POSITION.TOP_RIGHT });
        } finally {
            setLoading(false);
        }
    };

    const decodeBase64SVG = (base64String) => {
        if (!base64String) return '';
        let base64Data = base64String.includes('base64,')
            ? base64String.split('base64,')[1]
            : base64String;
        try {
            return atob(base64Data);
        } catch (error) {
            console.error('Error decoding:', error);
            return '';
        }
    };

    const limitText = (text, maxLength) => {
        if (!text) return '-';
        return text.length > maxLength ? text.substring(0, maxLength - 3) + '...' : text;
    };

    const handleModeChange = (mode) => {
        if (printMode === mode) {
            setPrintMode(null);
            setShowQrPreview(false);
            setPrintData({});
            return;
        }
        setPrintMode(mode);
        setShowQrPreview(false);
        setPrintData({});
        if (mode === "selected") {
            const maxQty = Number(pendingRequest.current?.params?.total_qty) || 1;
            setSelectedQty(maxQty);
        } else {
            setSelectedQty(1);
        }
    };
    const handleShowTemplate = () => {
        if (printMode === "selected") {
            const maxQty = Number(pendingRequest.current?.params?.total_qty) || 1;
            const qty = Number(selectedQty);

            if (!qty || qty < 1) {
                toast.error("Please enter a valid quantity", {
                    position: toast.POSITION.TOP_RIGHT,
                });
                return;
            }
            if (qty > maxQty) {
                toast.error(`Quantity cannot be more than ${maxQty}`, {
                    position: toast.POSITION.TOP_RIGHT,
                });
                return;
            }
        }
        fetchQRCode();
    };
    const effectiveQty = () => {
        const maxQty = Number(pendingRequest.current?.params?.total_qty) || 1;

        if (printMode === "each") return 1;
        if (printMode === "selected") {
            return Math.min(Number(selectedQty) || 1, maxQty);
        }
        return printData?.qty;
    };
    const printHandle = () => {
        const printWindow = window.open('', '_blank');

        if (!printWindow) {
            toast.error('Please allow pop-ups for this site', { position: toast.POSITION.TOP_RIGHT });
            return;
        }

        const companyName = limitText(printData?.company_data?.name, 45);
        const manufacturer = limitText(printData?.manufacturer, 30);
        const productName = limitText(printData?.name, 30);
        const productCode = limitText(printData?.code, 20);
        const qtyToPrint = effectiveQty();

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
                <head>
                    <title>Print QR Label</title>
                    <style>
                        * { margin: 0; padding: 0; box-sizing: border-box; }
                        @page { size: 4in 2in landscape; margin: 0mm; }
                        body {
                            margin: 0; padding: 0; background: white;
                            font-family: 'Segoe UI', 'Arial', sans-serif;
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                        .thermal-label {
                            width: 4in; height: 2in; background: white;
                            border: 1px solid #e0e0e0;
                            padding: 7px 10px; box-sizing: border-box;
                            display: flex; flex-direction: column;
                        }
                        .label-header {
                            font-size: 14px; font-weight: 700; text-align: center;
                            color:#000000; letter-spacing: 0.3px; text-transform: uppercase;
                            margin-bottom: 6px; margin-top: 6px;
                        }
                        .label-content {
                            display: flex; align-items:center; justify-content:space-between; gap: 10px;
                        }
                        .info-section { display: flex; flex-direction:column; }
                        .info-row { display: flex; margin-bottom: 5px; align-items: baseline; }
                        .info-label {
                            font-size: 11px; font-weight: 600; color: #000000;
                            width: 70px; text-transform: uppercase;
                        }
                        .info-value {
                            font-size: 11px; font-weight: 600; color: #000000;
                            word-break: break-word;
                        }
                        .qr-section {
                            width: 75px; display: flex; flex-direction: column;
                            align-items: center; justify-content: center;
                            background: #fafafa; border-radius: 6px;
                            padding: 4px; margin-right:5px;
                        }
                        .qr-wrapper img { width: 65px; height: 65px; }
                        .print-date {
                            font-size: 10px; color: #000000;
                            margin-top:3px 4px; text-align:end;
                        }
                    </style>
                </head>
                <body>
                    <div class="thermal-label">
                        <span class="label-header">
                            ${companyName || 'NEXUS DEVELOPMENT'}
                        </span>
                        <div class="label-content">
                            <div class="info-section">
                                <div class="info-row">
                                    <span class="info-label">NAME</span>
                                    <span class="info-value">${productName}</span>
                                </div>
                                <div class="info-row">
                                    <span class="info-label">CODE</span>
                                    <span class="info-value">${productCode}</span>
                                </div>
                                <div class="info-row">
                                    <span class="info-label">MANF.</span>
                                    <span class="info-value">${manufacturer}</span>
                                </div>
                                <div class="info-row">
                                    <span class="info-label">BATCH NO</span>
                                    <span class="info-value">${printData?.batch_number || '-'}</span>
                                </div>
                                <div class="info-row">
                                    <span class="info-label">EXPIRY DATE</span>
                                    <span class="info-value">${printData?.expiry_date && printData.expiry_date != null ? printData?.expiry_date : '-'}</span>
                                </div>
                                <div class="info-row">
                                    <span class="info-label">QTY</span>
                                    <span class="info-value">${qtyToPrint || '-'}</span>
                                </div>
                            </div>
                            <div class="qr-section">
                                <div class="qr-wrapper">
                                    ${decodeBase64SVG(printData?.qr_code)}
                                </div>
                            </div>
                        </div>
                        <span class="print-date">PRINTED: ${currentData}</span>
                    </div>
                    <script>
                        window.onload = function() {
                            setTimeout(function() {
                                window.print();
                                setTimeout(function() {
                                    window.close();
                                }, 500);
                            }, 300);
                        };
                    <\/script>
                </body>
            </html>
        `);
        printWindow.document.close();
    };

    const qrCodeLabelPrinter = () => {
        const qtyToPrint = effectiveQty();

        return (
            <div
                style={{
                    width: '100%',
                    minHeight: '200px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <div
                    style={{
                        width: '3.8in',
                        height: '1.9in',
                        background: '#fff',
                        border: '2px solid #000',
                        padding: '8px',
                        boxSizing: 'border-box',
                    }}
                >
                    <div>
                        <p
                            style={{
                                margin: 0,
                                fontSize: '12px',
                                fontWeight: '700',
                                textAlign: 'center',
                                paddingBottom: '4px',
                                borderBottom: '1px solid #ddd',
                                marginBottom: '4px',
                            }}
                        >
                            {limitText(printData?.company_data?.name, 45)}
                        </p>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div style={{ fontSize: '10px', width: '70%' }}>
                            <div style={{ margin: '2px 0', display: 'flex', gap: "5px" }}>
                                <span style={{ fontWeight: 'bold', width: '70px' }}>NAME</span>
                                <span><b>{limitText(printData?.name, 25)}</b></span>
                            </div>
                            <div style={{ margin: '2px 0', display: 'flex', gap: "5px" }}>
                                <span style={{ fontWeight: 'bold', width: '70px' }}>CODE</span>
                                <span><b>{limitText(printData?.code, 20)}</b></span>
                            </div>
                            <div style={{ margin: '2px 0', display: 'flex', gap: "5px" }}>
                                <span style={{ fontWeight: 'bold', width: '70px' }}>MANF.</span>
                                <span><b>{limitText(printData?.manufacturer, 25)}</b></span>
                            </div>
                            <div style={{ margin: '2px 0', display: 'flex', gap: "5px" }}>
                                <span style={{ fontWeight: 'bold', width: '70px' }}>BATCH NO</span>
                                <span><b>{limitText(printData?.batch_number, 15)}</b></span>
                            </div>
                            <div style={{ margin: '2px 0', display: 'flex', gap: "5px" }}>
                                <span style={{ fontWeight: 'bold', width: '70px' }}>EXPIRY DATE</span>
                                <span><b>{printData?.expiry_date && printData.expiry_date != null ? printData?.expiry_date : '-'}</b></span>
                            </div>
                            <div style={{ margin: '2px 0', display: 'flex', gap: "5px" }}>
                                <span style={{ fontWeight: 'bold', width: '70px' }}>QTY</span>
                                <span><b>{qtyToPrint || '-'}</b></span>
                            </div>
                        </div>
                        <div
                            style={{
                                width: '28%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <div
                                style={{
                                    width: '75px',
                                    height: '75px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                                dangerouslySetInnerHTML={{ __html: decodeBase64SVG(printData?.qr_code) }}
                            />
                        </div>
                    </div>

                    <p
                        style={{
                            fontSize: '10px',
                            fontWeight: '600',
                            marginBottom: "0px",
                            marginTop: "4px",
                            textAlign: "right",
                            paddingTop: "3px",
                            borderTop: "1px solid #eee",
                        }}
                    >
                        PRINT DATE : {currentData}
                    </p>
                </div>
            </div>
        );
    };
    const eachId = `${searchModalId}-each`;
    const selectedId = `${searchModalId}-selected`;
    return (
        <>
            <div className="modal fade" id={searchModalId} tabIndex="-1" ref={searchModalElem}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Print Slip</h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            ></button>
                        </div>

                        <div className="modal-body">
                            {loading ? (
                                <Loader />
                            ) : (
                                <>
                                    <div className="mb-3">
                                        <div className="form-check mb-2 d-flex align-items-center gap-3 flex-wrap">
                                            <div>
                                                <input
                                                    type="checkbox"
                                                    id={eachId}
                                                    checked={printMode === "each"}
                                                    disabled={printMode === "selected"}
                                                    onChange={() => handleModeChange("each")}
                                                    className="pt-2"
                                                />
                                                <label className="form-check-label ms-2" htmlFor={eachId}>
                                                    One Slip for each qty
                                                </label>
                                            </div>

                                            {printMode === "each" && (
                                                <button
                                                    type="button"
                                                    className="btn btn-primary"
                                                    onClick={handleShowTemplate}
                                                >
                                                    Show Template
                                                </button>
                                            )}
                                        </div>

                                        <div className="form-check mb-2 d-flex align-items-center gap-2 flex-wrap">
                                            <div>
                                                <input
                                                    type="checkbox"
                                                    id={selectedId}
                                                    checked={printMode === "selected"}
                                                    disabled={printMode === "each"}
                                                    onChange={() => handleModeChange("selected")}
                                                    className="pt-2"

                                                />
                                                <label className="form-check-label ms-2" htmlFor={selectedId}>
                                                    One slip for selected qty
                                                </label>
                                            </div>

                                            {printMode === "selected" && (
                                                <>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        max={Number(pendingRequest.current?.params?.total_qty) || undefined}
                                                        className="form-control"
                                                        style={{ width: "100px", height: "30px" }}
                                                        value={selectedQty}
                                                        onChange={(e) => {
                                                            const val = e.target.value;
                                                            const maxQty = Number(pendingRequest.current?.params?.total_qty) || 1;

                                                            if (val === "") {
                                                                setSelectedQty("");
                                                            } else {
                                                                const num = Number(val);
                                                                setSelectedQty(Math.max(1, Math.min(num, maxQty)));
                                                            }
                                                            setShowQrPreview(false);
                                                            setPrintData({});
                                                        }}
                                                        onKeyDown={(e) => {
                                                            const allowed = ["Backspace", "ArrowLeft", "ArrowRight", "Delete", "Tab"];
                                                            if (!allowed.includes(e.key) && !/^[0-9]$/.test(e.key)) {
                                                                e.preventDefault();
                                                            }
                                                        }}
                                                        placeholder="Qty"
                                                    />

                                                    <button
                                                        type="button"
                                                        className="btn btn-primary"
                                                        onClick={handleShowTemplate}
                                                    >
                                                        Show Template
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    {showQrPreview && (
                                        <div className="mt-3">
                                            {qrCodeLabelPrinter()}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                data-bs-dismiss="modal"
                            >
                                Close
                            </button>
                            {showQrPreview && (
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={printHandle}
                                >
                                    <TapIcon.FontAwesomeIcon
                                        icon={TapIcon.faPrint}
                                        className="me-1"
                                    />
                                    Print
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
});

export default ItemQrCodePrintModal;